import React from 'react';
import { Box, Tooltip, useTheme, alpha } from '@mui/material';

interface Props {
  name: string;
  /**
   * `true` when this row represents a class without sections — render an
   * italic, dashed, neutral chip to signal it's a synthetic/whole-class row.
   */
  isVirtual?: boolean;
  /** Optional tooltip shown over the chip. Pass for virtual rows to explain the synthetic state. */
  tooltip?: string;
}

/**
 * Lightweight section identity pill used in both the Class Teachers and
 * Subject Teachers tabs. Compact, low-chrome, info-tinted.
 */
const SectionChip: React.FC<Props> = ({ name, isVirtual = false, tooltip }) => {
  const theme = useTheme();

  const baseSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 0.875,
    py: 0.125,
    borderRadius: 999,
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: 0.1,
    lineHeight: 1.6,
    maxWidth: '100%',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const
  };

  const inner = isVirtual ? (
    <Box
      component="span"
      sx={{
        ...baseSx,
        fontStyle: 'italic',
        color: 'text.secondary',
        border: `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`
      }}
    >
      {name}
    </Box>
  ) : (
    <Box
      component="span"
      sx={{
        ...baseSx,
        color: 'info.dark',
        bgcolor: alpha(theme.palette.info.main, 0.06),
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`
      }}
    >
      {name}
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip}>{inner}</Tooltip> : inner;
};

export default SectionChip;
